import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpenIcon, ChevronRightIcon, FolderIcon } from '@heroicons/react/24/outline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getQuestionFolders } from '../../../services/questionService';
import SelectClassAndLesson from './SelectClassAndLesson/SelectClassAndLesson';
import Questions from './Questions/Questions';
import Final from './Final/Final';
import FormHeader from './FormHeader';
import { PrintedPassageNotice } from './Questions/EditNotices';
import PassagePanel from '../../../components/Passages/PassagePanel';
import { passageBooklets } from '../../../components/Passages/PassageLock';
import usePassage, { forgetPassage } from '../../../components/Passages/usePassage';
import { folderPath, isSubjectFolder } from '../../../utils/folders';
import { leavePage } from '../../QuestionPage/review/reviewUtils';
import WriteTaskReminder from '../../../components/QuestionFolders/tasks/WriteTaskReminder';

export default function CreateQuestion() {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState();
  const [searchParams] = useSearchParams();
  const folderParam = searchParams.get('folder');
  const passageParam = searchParams.get('passage');
  // A write task's "Write a question" (?task=<uuid>): its reminder shows that task.
  const taskParam = searchParams.get('task');
  // The passage's detail says whether an active booklet prints it now (then no new question can join it): read
  // afresh, not from a detail cached earlier in the visit. Declared before usePassage, whose effect runs after this
  // one (a component's effects run in order): on opening and for every other ?passage=.
  useEffect(() => {
    if (passageParam) forgetPassage(passageParam);
  }, [passageParam]);
  // The exam's subject the questions are created in (?folder=<uuid>); resolved from the folder list so that
  // an unknown/deleted folder, or an old backend without folders, simply creates the questions without one.
  // The questions take the folder's subject; `folderLabel` is "ТАТ · Biology".
  const [folder, setFolder] = useState(null);
  const [folderLabel, setFolderLabel] = useState('');
  // What the final view's save found missing (by question id): the questions step opens with it marked.
  const [returnedErrors, setReturnedErrors] = useState(null);
  // The passage the questions are written for (?passage=<uuid>, "Create a question for this passage"). If it
  // can't be loaded (unknown passage, old backend), the questions are simply created without one.
  const { data: passageDetail } = usePassage(passageParam);
  const passage = useMemo(
    () => (passageDetail ? { uuid: passageDetail.uuid, name: passageDetail.name } : null),
    [passageDetail],
  );

  // Written for a passage, the questions go into the passage's folder (they live where it does).
  const folderUuid = folderParam || passageDetail?.folder?.uuid || null;
  // An active BTS booklet prints the passage: the questions can be written and kept as drafts, not created now.
  const printedBy = useMemo(() => (passage ? passageBooklets(passageDetail) : []), [passage, passageDetail]);

  useEffect(() => {
    setFolder(null);
    if (!folderUuid) return undefined;
    const controller = new AbortController();
    getQuestionFolders({ signal: controller.signal })
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.results || [];
        const found = list.find((f) => f.uuid === folderUuid && isSubjectFolder(f)) || null;
        setFolder(found);
        setFolderLabel(found ? folderPath(found, new Map(list.map((f) => [f.uuid, f]))) : '');
      })
      .catch(() => {});
    return () => controller.abort();
  }, [folderUuid]);

  const handleData = useCallback((data) => {
    setData((prev) => ({ ...prev, ...data }));
  }, []);

  const steps = useMemo(
    () => [
      {
        title: t('my_questions.create.class_and_lesson'),
        component: (
          <SelectClassAndLesson
            next={() => setStep((prev) => prev + 1)}
            // The first step's Back leaves the form, to where it was opened from.
            back={() => leavePage(navigate, '/my-questions')}
            handleData={handleData}
            lockedSubject={folder?.subject || null}
            defaults={data}
          />
        ),
      },
      {
        title: t('my_questions.create.questions'),
        component: (
          <Questions
            next={() => {
              setReturnedErrors(null);
              setStep((prev) => prev + 1);
            }}
            back={() => setStep(0)}
            data={data}
            handleData={handleData}
            passage={passage}
            folder={folder}
            initialErrors={returnedErrors}
          />
        ),
      },
      {
        title: t('my_questions.create.final_view'),
        component: (
          <Final
            data={data}
            handleData={handleData}
            folder={folder}
            passage={passage}
            passageBooklets={printedBy}
            back={() => setStep(1)}
            onIncomplete={(errors) => {
              setReturnedErrors(errors);
              setStep(1);
            }}
          />
        ),
      },
    ],
    [handleData, navigate, t, data, folder, passage, printedBy, returnedErrors],
  );

  return (
    <div className="flex h-full min-w-0 flex-col gap-6 motion-safe:animate-fade-in">
      {/* The title with the form's guide; where the questions go (the folder, the passage) under it. */}
      <FormHeader
        title={t('my_questions.create.new_question')}
        mode="create"
        inSubject={isSubjectFolder(folder)}
        linked={!!passage}
      >
        {(folder || passage) && (
          <>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {folder && (
                <span
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20"
                  title={t('my_questions.create.folder_note', { name: folderLabel })}
                >
                  <FolderIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="truncate">{t('my_questions.create.in_folder', { name: folderLabel })}</span>
                </span>
              )}
              {passage && (
                <span
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20"
                  title={t('passages.create_note')}
                >
                  <BookOpenIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="truncate">{t('passages.create_banner', { name: passage.name })}</span>
                </span>
              )}
            </div>
            <div className="mt-1.5 space-y-1 text-xs text-slate-500">
              {folder && <p>{t('my_questions.create.folder_note', { name: folderLabel })}</p>}
              {passage && <p>{t('passages.create_note')}</p>}
            </div>
          </>
        )}
      </FormHeader>
      {/* Said up front while writing (the questions step has "Save as draft and exit"; the first step does not); the
          final view says it next to the button it holds back. */}
      {printedBy.length > 0 && step < 2 && <PrintedPassageNotice booklets={printedBy} saveLater={step === 1} />}
      {/* What the author's task asks for, and the text to write the questions against, at hand on every step. */}
      {isSubjectFolder(folder) && <WriteTaskReminder folderUuid={folder.uuid} taskUuid={taskParam} />}
      <PassagePanel passage={passage} />
      <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:px-5">
        {steps.map((stp, i) => (
          <React.Fragment key={i}>
            {!!i && <ChevronRightIcon className="mx-1 h-4 w-4 text-slate-300 sm:mx-3" aria-hidden="true" />}
            <div
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition duration-200 ${
                step >= i
                  ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/25'
                  : 'bg-white text-slate-500 ring-1 ring-inset ring-slate-300'
              }`}
            >
              {i + 1}
            </div>
            {/* A step already passed can be opened again. */}
            {step > i ? (
              <button
                type="button"
                className="rounded text-sm font-medium text-slate-900 transition duration-200 hover:text-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                onClick={() => setStep(i)}
              >
                {stp.title}
              </button>
            ) : (
              <span
                aria-current={step === i ? 'step' : undefined}
                className={`text-sm font-medium ${step === i ? 'text-slate-900' : 'text-slate-500'}`}
              >
                {stp.title}
              </span>
            )}
          </React.Fragment>
        ))}
      </div>
      {steps[step].component}
    </div>
  );
}
